import React, { useState, useEffect } from "react";
import axios from "axios";
import AddIcon from "@mui/icons-material/Add";
import { Tooltip } from "@mui/material";


import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import "./ViewParameter.css";
import EditParameter from "./EditParameter";
import apiService from "../../utils/apiservice";
// ✅ TOASTIFY
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_URL = process.env.REACT_APP_API_DJANGO_URL;

const ViewParameter = () => {
  const [devices, setDevices] = useState([]);
  const [params, setParams] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [loading, setLoading] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [paramToDelete, setParamToDelete] = useState(null);

  const [editOpen, setEditOpen] = useState(false);
  const [paramToEdit, setParamToEdit] = useState(null);

  const [pendingKeys, setPendingKeys] = useState([]);
  const [thresholdOpen, setThresholdOpen] = useState(false);
  const [selectedParam, setSelectedParam] = useState(null);
  const [thresholdValue, setThresholdValue] = useState("");

  const [thresholds, setThresholds] = useState({
    upp_Hyd_thresh: "",
    low_hyd_thresh: "",
    upp_spr_thresh: "",
    low_spr_thresh: "",
  });
  const FORCE_THRESHOLD_KEYS = [
    "Low_Spr_Pr",
    "Upp_Hyd_Pr",
    "Upp_Spr_Pr",
    "Low_Hyd_Pr"
  ];

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);


  // Check if selected parameter already has a threshold
  const hasThreshold = () => {
    if (!selectedParam) return false;

    switch (selectedParam.key_name) {
      case "Upp_Hyd_Pr":
        return thresholds.upp_Hyd_thresh !== "";
      case "Low_Hyd_Pr":
        return thresholds.low_hyd_thresh !== "";
      case "Upp_Spr_Pr":
        return thresholds.upp_spr_thresh !== "";
      case "Low_Spr_Pr":
        return thresholds.low_spr_thresh !== "";
      default:
        return false;
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Reset page when device changes
  useEffect(() => {
    setPage(0);
  }, [selectedDeviceId]);



  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem("accessToken");

      const response = await apiService({
        url: `/api/get_customers/`,   // <-- adjust if your endpoint differs
        method: "GET",
        data: {},
      });

      const formatted = response.data.map((c) => ({
        id: c.tb_customer_id,
        name: c.customer_name,
      }));

      setCustomers(formatted);

      // auto-select first customer
      if (formatted.length > 0) {
        const firstCustomerId = formatted[0].id;

        setSelectedCustomerId(firstCustomerId);
        fetchDevices(firstCustomerId); // ✅ ADD THIS
      }
    } catch (err) {
      console.error("Error fetching customers:", err);
      toast.error("Failed to fetch customers");
    }
  };

  // ✅ FETCH DEVICES
  const fetchDevices = async (customerId) => {
    try {
      setLoading(true);

      const token = localStorage.getItem("accessToken");
      // const customerId = localStorage.getItem("customer_id");

      if (!customerId) return;


      const response = await apiService({
        url: `/api/customer_devices/${customerId}/?page=0&pageSize=1000`,
        method: "GET",
        data: {}, // ✅ keep
      });

      const formattedDevices = response.data.data.data.map((dev) => ({
        id: dev.id.id,
        name: dev.name,
      }));

      setDevices(formattedDevices);

      // ✅ AUTO SELECT FIRST DEVICE
      if (formattedDevices.length > 0) {
        const firstDeviceId = formattedDevices[0].id;

        setSelectedDeviceId(firstDeviceId);

        await fetchParameters(firstDeviceId);
        await fetchDeviceThresholds(firstDeviceId); // ✅ ADD THIS
      }

    } catch (error) {
      console.error("Error fetching devices:", error);
      toast.error("Failed to fetch devices");
    } finally {
      setLoading(false);
    }
  };

  // ✅ FETCH PARAMETERS
  const fetchParameters = async (deviceId) => {
    try {
      setLoading(true);

      const token = localStorage.getItem("accessToken");

      // const response = await axios({
      //   url: `${API_URL}/api/get_device_parameters/${deviceId}/`,
      //   method: "GET",
      //   headers: {
      //     Authorization: `Bearer ${token}`,
      //     Accept: "application/json",
      //     "Content-Type": "application/json",
      //   },
      //   data: {},
      // });

      const response = await apiService({
        url: `/api/get_device_parameters/${deviceId}/`,
        method: "GET",
        data: {}, // ✅ keep
      });

      const formattedParams = (response.data.parameters || []).map((p) => ({
        id: p.parameter_id,
        display_name: p.display_name,
        key_name: p.key_name,
      }));

      setParams(formattedParams);

    } catch (error) {
      console.error("Error fetching parameters:", error);
      toast.error("Failed to fetch parameters");
      setParams([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingKeynames = async () => {
    try {
      const token = localStorage.getItem("accessToken");

      const response = await axios.get(
        `${API_URL}/api/get_pending_keynames/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // only take keys where is_alarm_threshold = true
      const filteredKeys = response.data.data
        .filter((item) => item.is_alarm_threshold)
        .map((item) => item.key_name);

      setPendingKeys(filteredKeys);

    } catch (error) {
      console.error("Error fetching pending keys:", error);
    }
  };




  const fetchDeviceThresholds = async (deviceId) => {
    try {
      const token = localStorage.getItem("accessToken");

      const response = await axios.get(
        `${API_URL}/api/get_device_thresholds/${deviceId}/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );

      // Convert array of {key, value} to thresholds object
      const data = response.data;
      const mapped = {
        upp_Hyd_thresh: data.find(d => d.key === "upper_hydrant_threshold")?.value || "",
        low_hyd_thresh: data.find(d => d.key === "lower_hydrant_threshold")?.value || "",
        upp_spr_thresh: data.find(d => d.key === "upper_sprinkler_threshold")?.value || "",
        low_spr_thresh: data.find(d => d.key === "lower_sprinkler_threshold")?.value || "",
      };

      setThresholds(mapped);

      return mapped; // return mapped so we can use it immediately

    } catch (err) {
      console.error("Error fetching thresholds:", err);
      toast.error("Failed to fetch thresholds");
      setThresholds({
        upp_Hyd_thresh: "",
        low_hyd_thresh: "",
        upp_spr_thresh: "",
        low_spr_thresh: "",
      });
      return {};
    }
  };

  useEffect(() => {
    fetchCustomers();
    // fetchDevices();
    fetchPendingKeynames();
  }, []);

  const getThresholdValue = (param) => {
    switch (param.key_name) {
      case "Upp_Hyd_Pr":
        return thresholds.upp_Hyd_thresh || "NA";
      case "Low_Hyd_Pr":
        return thresholds.low_hyd_thresh || "NA";
      case "Upp_Spr_Pr":
        return thresholds.upp_spr_thresh || "NA";
      case "Low_Spr_Pr":
        return thresholds.low_spr_thresh || "NA";
      default:
        return "NA";
    }
  };

  const handleCustomerChange = async (e) => {
    const customerId = e.target.value;
    setSelectedCustomerId(customerId);

    await fetchDevices(customerId);
  };

  const handleDeviceChange = async (e) => {
    const deviceId = e.target.value;
    setSelectedDeviceId(deviceId);

    fetchParameters(deviceId);
    await fetchDeviceThresholds(deviceId); // ✅ ADD THIS
  };

  // ✅ DELETE CLICK
  const handleDeleteClick = (param) => {
    setParamToDelete(param);
    setDeleteOpen(true);
  };

  // ✅ DELETE API
  const handleConfirmDelete = async () => {
    try {
      const token = localStorage.getItem("accessToken");

      // const response = await axios({
      //   url: `${API_URL}/api/delete_parameter/${paramToDelete.id}/`,
      //   method: "DELETE",
      //   headers: {
      //     Authorization: `Bearer ${token}`,
      //     Accept: "application/json",
      //     "Content-Type": "application/json",
      //   },
      //   data: {},
      // });

      const response = await apiService({
        url: `/api/delete_parameter/${paramToDelete.id}/`,
        method: "DELETE",
        data: {}, // ✅ keep
      });

      if (response.status === 200) {
        setParams((prev) =>
          prev.filter((p) => p.id !== paramToDelete.id)
        );

        setDeleteOpen(false);
        setParamToDelete(null);

        toast.success("Parameter deleted successfully");
      }

    } catch (error) {
      console.error("Delete error:", error.response?.data || error.message);

      toast.error(
        error.response?.data?.error || "Failed to delete parameter"
      );
    }
  };

  // ✅ EDIT CLICK
  const handleEditClick = (param) => {
    setParamToEdit(param);
    setEditOpen(true);
  };

  // ✅ UPDATE API
  const handleSaveEdit = async (updatedParam) => {
    try {
      const token = localStorage.getItem("accessToken");

      // const response = await axios({
      //   url: `${API_URL}/api/update_parameter/${updatedParam.id}/`,
      //   method: "POST",
      //   headers: {
      //     Authorization: `Bearer ${token}`,
      //     Accept: "application/json",
      //     "Content-Type": "application/json",
      //   },
      //   data: {
      //     display_name: updatedParam.display_name,
      //     key_name: updatedParam.key_name,
      //   },
      // });

      const response = await apiService({
        url: `/api/update_parameter/${updatedParam.id}/`,
        method: "POST",
        data: {
          display_name: updatedParam.display_name,
          key_name: updatedParam.key_name,
        },
      });

      if (response.status === 200) {
        setParams((prev) =>
          prev.map((p) =>
            p.id === updatedParam.id ? updatedParam : p
          )
        );

        setEditOpen(false);
        setParamToEdit(null);

        toast.success("Parameter updated successfully");
      }

    } catch (error) {
      console.error("Update error:", error.response?.data || error.message);

      toast.error(
        error.response?.data?.error || "Failed to update parameter"
      );
    }
  };
  return (
    <Box className="view-parameter-container">



      {/* 🔽 DEVICE DROPDOWN */}
      <Box
        sx={{
          display: "flex",
          gap: 5,
          mb: 2,
        }}
      >
        <TextField
          select
          label="Select Customer"
          value={selectedCustomerId}
          onChange={handleCustomerChange}
          sx={{
            width: { xs: "80%", sm: 300 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              backgroundColor: "#fff",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderRadius: "10px",
            },
          }}
        >
          {customers.map((cust) => (
            <MenuItem key={cust.id} value={cust.id}>
              {cust.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Select Device"
          value={selectedDeviceId}
          onChange={handleDeviceChange}
          sx={{
            width: { xs: "80%", sm: 300 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              backgroundColor: "#fff",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderRadius: "10px",
            },
          }}
        >
          {devices.map((dev) => (
            <MenuItem key={dev.id} value={dev.id}>
              {dev.name}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {/* 📊 TABLE */}
      <TableContainer component={Paper} >
        <Table size="small" className="site-table">
          <TableHead>
            <TableRow>
              <TableCell>Sr No</TableCell>
              <TableCell>Display Name</TableCell>
              <TableCell>Key Name</TableCell>
              <TableCell align="center">Threshold</TableCell>
              <TableCell align="center" sx={{ width: 150 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : selectedDeviceId ? (
              params.length > 0 ? (
                params
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((param, index) => {

                    const isThresholdParam =
                      pendingKeys.includes(param.key_name) ||
                      FORCE_THRESHOLD_KEYS.includes(param.key_name);

                    return (
                      <TableRow key={param.id}>
                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>

                        <TableCell>{param.display_name}</TableCell>

                        <TableCell>
                          <code>{param.key_name}</code>
                        </TableCell>
                        <TableCell align="center">
                          {getThresholdValue(param)}
                        </TableCell>

                        <TableCell align="center">
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "flex-start", // 👈 key change
                              alignItems: "center",
                              gap: 1,
                              width: "100%",
                              pl: 4 // optional padding to center visually
                            }}
                          >

                            {/* ✏️ EDIT */}
                            <IconButton
                              color="primary"
                              onClick={() => handleEditClick(param)}
                            >
                              <EditIcon />
                            </IconButton>

                            {/* 🗑 DELETE */}
                            <IconButton
                              color="error"
                              onClick={() => handleDeleteClick(param)}
                            >
                              <DeleteIcon />
                            </IconButton>

                            {/* ⚠️ THRESHOLD */}
                            {isThresholdParam && (
                              <Tooltip title="Set Threshold">
                                <IconButton
                                  color="warning"
                                  size="small"
                                  onClick={async () => {
                                    setSelectedParam(param);

                                    const fetchedThresholds = await fetchDeviceThresholds(selectedDeviceId);

                                    // set thresholdValue based on param.key_name
                                    switch (param.key_name) {
                                      case "Upp_Hyd_Pr":
                                        setThresholdValue(fetchedThresholds.upp_Hyd_thresh);
                                        break;
                                      case "Low_Hyd_Pr":
                                        setThresholdValue(fetchedThresholds.low_hyd_thresh);
                                        break;
                                      case "Upp_Spr_Pr":
                                        setThresholdValue(fetchedThresholds.upp_spr_thresh);
                                        break;
                                      case "Low_Spr_Pr":
                                        setThresholdValue(fetchedThresholds.low_spr_thresh);
                                        break;
                                      default:
                                        setThresholdValue("");
                                    }

                                    setThresholdOpen(true);
                                  }}
                                >
                                  <AddIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No parameters found
                  </TableCell>
                </TableRow>
              )
            ) : (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  Please select a device
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

       
      </TableContainer>
       {/* 📄 PAGINATION */}
        {!loading && params.length > 0 && (
          <div className="pagination-bar">
            <div className="rows-select">
              Rows per page:
              <select value={rowsPerPage} onChange={handleChangeRowsPerPage}>
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
              </select>
            </div>

            <div className="page-info">
              {page * rowsPerPage + 1}-
              {Math.min((page + 1) * rowsPerPage, params.length)} of {params.length}
            </div>

            <div className="page-arrows">
              <button
                disabled={page === 0}
                onClick={() => setPage(page - 1)}
              >
                ‹
              </button>

              <button
                disabled={(page + 1) * rowsPerPage >= params.length}
                onClick={() => setPage(page + 1)}
              >
                ›
              </button>
            </div>
          </div>
        )}

      {/* ✏️ EDIT */}
      <EditParameter
        open={editOpen}
        onClose={() => setEditOpen(false)}
        parameter={paramToEdit}
        onSave={handleSaveEdit}
      />

      {/* 🗑 DELETE */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete Parameter</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure to delete <b>{paramToDelete?.display_name}</b> ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ⚠️ THRESHOLD POPUP */}
      <Dialog open={thresholdOpen} onClose={() => setThresholdOpen(false)}>
        <DialogTitle>Set Threshold</DialogTitle>

        <DialogContent>
          <DialogContentText>
            Enter threshold for <b>{selectedParam?.display_name}</b>
          </DialogContentText>

          <TextField
            fullWidth
            margin="normal"
            label="Threshold Value"
            value={thresholdValue}
            onChange={(e) => {
              const value = e.target.value;

              // Allow digits + optional decimal
              const validFormat = /^\d*\.?\d*$/;

              // Count only digits (ignore dot)
              const digitCount = value.replace(/\D/g, "").length;

              if (validFormat.test(value) && digitCount <= 4) {
                setThresholdValue(value);
              }
            }}
            inputProps={{
              inputMode: "decimal",
              placeholder: "e.g. 99.99"
            }}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setThresholdOpen(false)}>
            Cancel
          </Button>

          <Button
            variant="contained"
            color={hasThreshold() ? "secondary" : "primary"}
            onClick={async () => {
              try {
                const token = localStorage.getItem("accessToken");

                // Copy thresholds and update only the selected param
                const updatedThresholds = { ...thresholds };

                switch (selectedParam.key_name) {
                  case "Upp_Hyd_Pr":
                    updatedThresholds.upp_Hyd_thresh = thresholdValue;
                    break;
                  case "Low_Hyd_Pr":
                    updatedThresholds.low_hyd_thresh = thresholdValue;
                    break;
                  case "Upp_Spr_Pr":
                    updatedThresholds.upp_spr_thresh = thresholdValue;
                    break;
                  case "Low_Spr_Pr":
                    updatedThresholds.low_spr_thresh = thresholdValue;
                    break;
                  default:
                    break;
                }

                // Decide API based on whether threshold exists
                const apiUrl = hasThreshold()
                  ? `${API_URL}/api/update_device_thresholds/${selectedDeviceId}/`
                  : `${API_URL}/api/set_device_thresholds/${selectedDeviceId}/`;

                await axios.post(apiUrl, updatedThresholds, {
                  headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                    Accept: "application/json",
                  },
                });

                toast.success(hasThreshold() ? "Threshold updated" : "Threshold created");

                // Update local state
                setThresholds(updatedThresholds);
                setThresholdOpen(false);
                setThresholdValue("");
                setSelectedParam(null);

                fetchParameters(selectedDeviceId);

              } catch (err) {
                toast.error("Failed to save threshold");
                console.error(err.response?.data || err.message);
              }
            }}
          >
            {hasThreshold() ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 🔔 TOAST */}
      <ToastContainer position="top-right" autoClose={3000} />

    </Box>
  );
};

export default ViewParameter;